import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { getCanonicalSearchUrl } from '@eventbrite/redux-destination';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Tile, TileGroup } from '../../../../components/Tiles';
import {
    BUSINESS_EVENTBRITE_CATEGORY,
    CATEGORY_LONG_NAME_ICON_MAP,
    EVENTBRITE_CATEGORY_MAPPING,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
    HEALTH_EVENTBRITE_CATEGORY,
    HOBBIES_EVENTBRITE_CATEGORY,
    MUSIC_EVENTBRITE_CATEGORY,
    PERFORMING_EVENTBRITE_CATEGORY,
    SEASONAL_EVENTBRITE_CATEGORY,
    SPORTS_EVENTBRITE_CATEGORY,
} from '../../../../constants';
import { UserShape } from '../../../../types';
import { HomeEngagementExperimentTrackingLocations } from '../../constants/analytics';
import { logToStatsigHomeEngagement } from '../../utils/analytics';
import './CategoryTiles.scss';

interface CategoryTile {
    key: string;
    name: GenericLazyString;
    icon: any;
}

const CATEGORIES: CategoryTile[] = [
    {
        key: MUSIC_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CATEGORY_MAPPING[MUSIC_EVENTBRITE_CATEGORY],
        icon: CATEGORY_LONG_NAME_ICON_MAP[MUSIC_EVENTBRITE_CATEGORY],
    },
    {
        key: PERFORMING_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CATEGORY_MAPPING[PERFORMING_EVENTBRITE_CATEGORY],
        icon: CATEGORY_LONG_NAME_ICON_MAP[PERFORMING_EVENTBRITE_CATEGORY],
    },
    {
        key: SEASONAL_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CATEGORY_MAPPING[SEASONAL_EVENTBRITE_CATEGORY],
        icon: CATEGORY_LONG_NAME_ICON_MAP[SEASONAL_EVENTBRITE_CATEGORY],
    },
    {
        key: HEALTH_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CATEGORY_MAPPING[HEALTH_EVENTBRITE_CATEGORY],
        icon: CATEGORY_LONG_NAME_ICON_MAP[HEALTH_EVENTBRITE_CATEGORY],
    },
    {
        key: HOBBIES_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CATEGORY_MAPPING[HOBBIES_EVENTBRITE_CATEGORY],
        icon: CATEGORY_LONG_NAME_ICON_MAP[HOBBIES_EVENTBRITE_CATEGORY],
    },
    {
        key: BUSINESS_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CATEGORY_MAPPING[BUSINESS_EVENTBRITE_CATEGORY],
        icon: CATEGORY_LONG_NAME_ICON_MAP[BUSINESS_EVENTBRITE_CATEGORY],
    },
    {
        key: FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CATEGORY_MAPPING[FOOD_AND_DRINK_EVENTBRITE_CATEGORY],
        icon: CATEGORY_LONG_NAME_ICON_MAP[FOOD_AND_DRINK_EVENTBRITE_CATEGORY],
    },
    {
        key: SPORTS_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CATEGORY_MAPPING[SPORTS_EVENTBRITE_CATEGORY],
        icon: CATEGORY_LONG_NAME_ICON_MAP[SPORTS_EVENTBRITE_CATEGORY],
    },
];

const buildBBox = ({
    latitude,
    longitude,
}: {
    latitude: number;
    longitude: number;
}) => ({
    sw: { lng: longitude - 0.4, lat: latitude - 0.3 },
    ne: { lng: longitude + 0.4, lat: latitude + 0.3 },
});

interface CategoryTilesProps {
    locationSlug?: string;
    latitude?: number;
    longitude?: number;
    isFeLoading?: boolean;
    user: UserShape;
}

const CategoryTilesRaw: React.FunctionComponent<CategoryTilesProps> = (
    props,
) => {
    const { locationSlug, latitude, longitude, isFeLoading, user } = props;
    const hasLocation = !!locationSlug || (!!latitude && !!longitude);

    if (!isFeLoading && !hasLocation) {
        return null;
    }

    return (
        <div className="home__category-tiles">
            <h3 className="home__category-tiles--title eds-text-hs">
                {gettext('Check out trending categories')}
            </h3>
            <TileGroup tiles={CATEGORIES}>
                {(tileContent) => {
                    if (isFeLoading) {
                        return (
                            <Tile
                                key={tileContent?.key}
                                name={tileContent?.name}
                                icon={tileContent?.icon}
                            />
                        );
                    }

                    if (locationSlug) {
                        return (
                            <Link
                                to={`/b/${locationSlug}/${
                                    EVENTBRITE_CATEGORY_TO_REFINEMENT[
                                        tileContent?.key || ''
                                    ]
                                }/`}
                                key={tileContent?.key}
                                onClick={() => {
                                    logToStatsigHomeEngagement(
                                        HomeEngagementExperimentTrackingLocations.TRENDING,
                                        !!user.isAuthenticated,
                                    );
                                    trackAnalyticsEvent({
                                        category: 'home',
                                        action: 'CategoryCardClicked',
                                        label: tileContent?.name,
                                    });
                                    window.scrollTo(0, 0);
                                }}
                            >
                                <Tile
                                    name={tileContent?.name}
                                    icon={tileContent?.icon}
                                />
                            </Link>
                        );
                    }

                    if (latitude && longitude) {
                        return (
                            <a
                                key={tileContent?.key}
                                href={getCanonicalSearchUrl({
                                    tags: [tileContent?.key],
                                    bbox: buildBBox({
                                        latitude: latitude as number,
                                        longitude: longitude as number,
                                    }),
                                })}
                                onClick={() => {
                                    logToStatsigHomeEngagement(
                                        HomeEngagementExperimentTrackingLocations.TRENDING,
                                        !!user.isAuthenticated,
                                    );
                                    trackAnalyticsEvent({
                                        category: 'home',
                                        action: 'CategoryCardClicked',
                                        label: tileContent?.name,
                                    });
                                }}
                            >
                                <Tile
                                    name={tileContent?.name}
                                    icon={tileContent?.icon}
                                />
                            </a>
                        );
                    }

                    return <div />;
                }}
            </TileGroup>
        </div>
    );
};

const _mapStateToProps = (state: {
    location?: {
        slug?: string;
        latitude?: string;
        longitude?: string;
    };
    app: {
        loadingState: { isFeLoading: boolean };
    };
    user: UserShape;
}) => ({
    locationSlug: state.location?.slug,
    latitude: state.location?.latitude,
    longitude: state.location?.longitude,
    isFeLoading: state.app.loadingState?.isFeLoading,
    user: state.user,
});
export const CategoryTiles = connect(_mapStateToProps)(CategoryTilesRaw);
